import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  Output,
  Signal,
  ViewEncapsulation,
} from '@angular/core';
import {EControlActions, EsvgFiles, IControlDataChanged, IControlObject, INITIAL_STATE_TOKEN} from 'frontier/nucleus';
import {
  ITableAction,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
} from 'frontier/browserkit';
import {GridOptions} from "ag-grid-community";
import {ProcessOverviewStore} from '../../../service/process-overview.store';
import AppControlGuids from '../../../../../../api/ControlGuids';
import {Observable, of, tap} from "rxjs";
import {ICart, IPdfOverlay, IProcessState} from "../../../service/state.interface";
import {filter} from "rxjs/operators";
import {IPdfOverlayToggleEvent} from "../../../carts/pdf-overlay-toggle-event.interface";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ECalculusStateFilter} from "../../../../../config/state-filter.enum";

@Component({
  selector: 'kpi4me-trapo-detail',
  templateUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  encapsulation: ViewEncapsulation.Emulated,
  standalone: true,
  imports: [TableControlModule],
  providers: [
    {provide: INITIAL_STATE_TOKEN, useValue: {filter: null}},
    {provide: TableControlComponent, useExisting: TrapoDetailComponent},
  ],
})
export class TrapoDetailComponent extends TableControlComponent {
  private _snackbar: MatSnackBar = inject(MatSnackBar);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;
  pdfOverlay: Signal<IPdfOverlay> = this._processOverviewService.pdfOverlay;
  isPdfLoading: Signal<boolean> = this._processOverviewService.isPdfLoading;
  selectedProcess: Signal<IProcessState> = this._processOverviewService.selectedProcess;

  @Output() detailTrapoDocumentToggleTriggered: EventEmitter<IPdfOverlayToggleEvent>
    = new EventEmitter<IPdfOverlayToggleEvent>();

  override GUID: string = AppControlGuids.TTrapoList;
  override tableName: string = 'Rechnungsdokumente';
  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    rowSelection: 'single',
  };

  constructor() {
    super();
    effect((): void => {
      const selectedCart: ICart = this.selectedCart();
      if (selectedCart) {
        this.patchFilter({
          parent: selectedCart.obj,
          typename: 'cart',
        });
      }
    }, {allowSignalWrites: true});

    this.subs.add(
      this.controlStore.controlDataChanged$.pipe(
        filter((evt: IControlDataChanged): boolean => {
          return new Map([
            [AppControlGuids.TCartDocumentList, new Set([EControlActions.changeLine, EControlActions.delete])],
          ]).get(evt.GUID)?.has(evt.changeType);
        }),
        tap(() => this.changeAndFetchInstance())).subscribe(),
    );
  }

  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    {
      displayName: 'Löschen',
      controlAction: EControlActions.delete,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: false,
      color: 'warn',
      icon: EsvgFiles.delete,
      isHidden: () => {
        return this.selectedProcess().category === ECalculusStateFilter.arrangeInvoice ||
          this.selectedProcess().category === ECalculusStateFilter.monitorPayment ||
          this.selectedProcess().category === ECalculusStateFilter.clientAccessMessages ||
          this.selectedProcess()?.state === ECalculusStateFilter.createInvoiceOk;
      },
    },
    {
      displayName: (): string => {
        return this.pdfOverlay()?.type === 'detailTrapoDocument' ? 'Ausblenden' : 'Anzeigen'
      },
      controlAction: EControlActions.invoicePreview,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.search,
      tooltip: 'Blende das Dokument ein.',
      tooltipDelay: 1000,
      action: () => this.toggleDetailTrapoDocumentPreview(),
      isDisabled: (): boolean => {
        return this.isPdfLoading() === true;
      },
    },
  ]);

  toggleDetailTrapoDocumentPreview(): Observable<null> {
    let documentObj: IControlObject;
    this.gridApi?.getSelectedRows()[0]?.apiRow?.cols.forEach((row: any) => {
      if (row.value && typeof row.value === 'object' && row.value.type === 'TDocument') {
        documentObj = row.value;
      }
    })

    if (documentObj) {
      this.detailTrapoDocumentToggleTriggered.emit({
        type: 'detailTrapoDocument',
        data: {obj: documentObj, apiInstance: this.apiInstance()},
      });
    } else {
      this._snackbar.open('Es wurde leider kein Dokument an der Tabellenzeile gefunden.', 'ok', {
        verticalPosition: 'top',
        duration: 3000,
      });
    }

    return of(null);
  }
}
